import { useToast } from "@ligo/layout-design-core";
import { tenSeconds } from "consts";
import {
  BaseProviderType,
  PermissionProviderType,
  SoftPhoneManagerType,
} from "models";
import { useProfile } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { windowOpenSessionCleaner } from "utils";
import { SoftphoneIframe } from "./styles";

type SoftphoneContentType = Window & {
  SoftPhoneManager: SoftPhoneManagerType;
};

type SoftphoneContextType = {
  mute: boolean;
  hold: boolean;
  inReload: boolean;
  hasEmbedActions: boolean;
  onReloadWebPhone: () => void;
  resetSoftphoneActions: () => void;
  onMute: () => void;
  onHold: () => void;
};

const popupSize = { x: 600, y: 400 };
const idIframe = "softphone-iframe";

let webPhoneWindow: Window | null;

const SoftphoneContext = createContext({} as SoftphoneContextType);

const Provider = ({ children }: BaseProviderType) => {
  const [mute, setMute] = useState(false);
  const [hold, setHold] = useState(false);
  const [inReload, setInReload] = useState(false);
  const { user, pIdProject, isSagoCore } = useProfile();
  const { t } = useTranslation();
  const { warning } = useToast();
  const softphoneRef = useRef<HTMLIFrameElement>(null);

  const hasAccess = useMemo(
    () => user?.hasAccessAttend && user?.hasDialerAPI && user?.deUrlWebPhone,
    [user],
  );

  const isNotAdmin = useMemo(
    () => pIdProject && !isSagoCore,
    [pIdProject, isSagoCore],
  );

  const hasPermission = useMemo(
    () => hasAccess && isNotAdmin,
    [hasAccess, isNotAdmin],
  );

  const useIframe = useMemo(() => user?.mustUseIFrameWebPhone ?? false, [user]);

  const hasEmbedActions = useMemo(
    () => !!user?.deUrlWebPhone && useIframe,
    [user, useIframe],
  );

  const iframe = useMemo(() => {
    if (hasPermission && useIframe)
      return (
        <SoftphoneIframe
          id={idIframe}
          title="Softphone"
          ref={softphoneRef}
          src={user!.deUrlWebPhone}
          allow="camera;microphone"
        />
      );
  }, [hasPermission, useIframe, user]);

  useEffect(() => {
    if (hasPermission && useIframe) {
      if (navigator.mediaDevices)
        navigator.mediaDevices
          ?.getUserMedia({ audio: true })
          .catch(() =>
            warning({ description: t("alerts.micPermission"), fixed: true }),
          );
      else console.log("Browser does not support mediaDevices.");
    }
  }, [hasPermission, useIframe, warning, t]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (inReload) interval = setInterval(() => setInReload(false), tenSeconds);
    return () => clearInterval(interval);
  }, [inReload]);

  const onOpenWebPhone = useCallback(
    (reopen?: boolean) => {
      if (webPhoneWindow?.closed === false && !reopen) webPhoneWindow.focus();
      else {
        if (reopen) webPhoneWindow?.close();

        const { x, y } = popupSize;
        const left = window.screen.width / 2 - x / 2;
        const top = window.screen.height / 2 - y / 2;

        webPhoneWindow = windowOpenSessionCleaner(
          user?.deUrlWebPhone,
          "WebPhone",
          `$width=${x},height=${y},left=${left},top=${top}`,
        );

        webPhoneWindow?.location.reload();
      }
    },
    [user],
  );

  useEffect(() => {
    const onBeforeunload = () => webPhoneWindow?.close();

    if (hasPermission && !useIframe) onOpenWebPhone();

    window.addEventListener("beforeunload", onBeforeunload);

    return () => {
      webPhoneWindow?.close();
      window.removeEventListener("beforeunload", onBeforeunload);
    };
  }, [hasPermission, useIframe, onOpenWebPhone]);

  const onReloadWebPhone = useCallback(() => {
    if (useIframe) {
      if (softphoneRef.current) softphoneRef.current.src += "";
    } else onOpenWebPhone(true);
    setInReload(true);
  }, [useIframe, onOpenWebPhone]);

  const getManager = useCallback(() => {
    const iframe = document.getElementById(idIframe) as HTMLIFrameElement;
    if (iframe?.contentWindow && hasEmbedActions) {
      const { SoftPhoneManager } = iframe.contentWindow as SoftphoneContentType;
      return SoftPhoneManager;
    }
  }, [hasEmbedActions]);

  const resetSoftphoneActions = useCallback(() => {
    const manager = getManager();
    if (manager) {
      setMute(false);
      manager?.unmute();

      setHold(false);
      manager?.unhold();
    }
  }, [getManager]);

  const onMute = useCallback(() => {
    const manager = getManager();
    setMute((current) => {
      if (current) manager?.unmute();
      else manager?.mute();

      return !current;
    });
  }, [getManager]);

  const onHold = useCallback(() => {
    const manager = getManager();
    setHold((current) => {
      if (current) manager?.unhold();
      else manager?.hold();

      return !current;
    });
  }, [getManager]);

  return (
    <SoftphoneContext.Provider
      value={{
        mute,
        hold,
        inReload,
        hasEmbedActions,
        onReloadWebPhone,
        resetSoftphoneActions,
        onMute,
        onHold,
      }}
    >
      {children}
      {iframe}
    </SoftphoneContext.Provider>
  );
};

export const SoftphoneProvider = ({
  hasPermission = true,
  ...args
}: PermissionProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useSoftphone = () => useContext(SoftphoneContext);
